import type { ErrorEvent } from "@sentry/nextjs";
import { APP_VERSION } from "@aluume/shared/lib/version";

// Shared Sentry init options for server / edge / client.
//
// PII posture (load-bearing): the Aluume guardrails forbid customer PII leaving
// controlled systems, and this Sentry org is in the US region. So we send the
// MINIMUM — errors only, no PII — and scrub every event before it leaves.
//
// Activation is gated: Sentry is enabled ONLY when a DSN is present AND we're in
// production. The shared dev server (NODE_ENV=development, no DSN) therefore runs
// Sentry as a complete no-op — it can't be destabilised by this.

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

/** The neighbour link carries its secret in the path (/n/<token>); never ship it to Sentry. */
const redactPathToken = (s: string): string => s.replace(/\/n\/[^/?#\s"']+/g, "/n/[token]");

/** Strip request metadata and user identity that can carry emails/names/tokens. */
function scrub(event: ErrorEvent): ErrorEvent {
  if (typeof event.transaction === "string") event.transaction = redactPathToken(event.transaction);
  for (const b of event.breadcrumbs ?? []) {
    if (typeof b.message === "string") b.message = redactPathToken(b.message);
    for (const k of ["url", "from", "to"]) {
      const v = b.data?.[k];
      if (typeof v === "string") b.data![k] = redactPathToken(v);
    }
  }
  if (event.request) {
    delete event.request.cookies;
    delete event.request.headers;
    delete event.request.data;
    delete event.request.query_string;
    if (typeof event.request.url === "string") event.request.url = redactPathToken(event.request.url.split("?")[0]);
  }
  // Keep ONLY the opaque staff auth UUID. Without a user Sentry reports "users affected: 0"
  // on every issue forever — the first number you read when triaging, and it made every error
  // look like it hit nobody. This is the id alone: no email, name, phone, IP, cookies, headers,
  // body or query string, all of which are still stripped above/below. It is STAFF identity
  // (set by <SentryUserSync> on the authenticated staff layout), never a customer's.
  // Before release 221 this was an unconditional `delete event.user`, which silently made
  // <SentryUserSync> dead code.
  event.user = event.user?.id ? { id: event.user.id } : undefined;
  return event;
}

export const sentryShared = {
  dsn,
  // Tie every event to a deploy. Without this, 24 of 25 issues reported release "** NONE **",
  // so "did this start in 219?" — the most useful triage question there is — was unanswerable.
  // APP_VERSION is the same integer /api/version serves and is bumped on every prod deploy, so
  // a Sentry release maps 1:1 to a release everyone already talks about. The build passes the
  // SAME value to the source-map upload (see next.config.ts), which previously ran with
  // `--release undefined` and so had nothing to match events against.
  release: String(APP_VERSION),
  enabled: !!dsn && process.env.NODE_ENV === "production",
  // Errors only — no performance traces (less data, less PII surface, less quota).
  tracesSampleRate: 0,
  // Never auto-attach IPs / cookies / headers.
  sendDefaultPii: false,
  beforeSend: (event: ErrorEvent): ErrorEvent => scrub(event),
};
